'use client'

import { forwardRef, type ReactNode } from 'react'
import { motion, useReducedMotion, type HTMLMotionProps } from 'motion/react'

const spring = { type: 'spring' as const, stiffness: 420, damping: 30, mass: 0.65 }

export const MicroButton = forwardRef<HTMLButtonElement, HTMLMotionProps<'button'>>(function MicroButton(
  { children, whileHover, whileTap, transition, ...props },
  ref,
) {
  const reduceMotion = useReducedMotion()
  return (
    <motion.button
      ref={ref}
      whileHover={reduceMotion ? undefined : (whileHover ?? { y: -1 })}
      whileTap={reduceMotion ? undefined : (whileTap ?? { scale: 0.965, y: 1 })}
      transition={transition ?? spring}
      {...props}
    >
      {children}
    </motion.button>
  )
})

interface FadeUpProps {
  children: ReactNode
  className?: string
  delay?: number
  yOffset?: number
}

/** Adapted from Amicro's FadeUp primitive, with reduced-motion support. */
export function FadeUp({ children, className, delay = 0, yOffset = 14 }: FadeUpProps) {
  const reduceMotion = useReducedMotion()
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: yOffset }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.48, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  )
}

interface CardDeckProps {
  labels?: string[]
  className?: string
}

/** A content-aware adaptation of Amicro's five-card arc for the flash-card library. */
export function CardDeck({ labels = ['错题', '公式', '方法', '笔记', '总结'], className = '' }: CardDeckProps) {
  const reduceMotion = useReducedMotion()
  return (
    <motion.div
      className={`soft-card-deck ${className}`}
      initial="rest"
      animate="rest"
      whileHover={reduceMotion ? undefined : 'open'}
      whileFocus={reduceMotion ? undefined : 'open'}
      aria-hidden="true"
    >
      {labels.slice(0, 5).map((label, index) => {
        const distance = index - 2
        return (
          <motion.div
            key={`${label}-${index}`}
            className="soft-card-deck-item"
            style={{ zIndex: 5 - Math.abs(distance) }}
            variants={{
              rest: { x: distance * 5, y: Math.abs(distance) * 2, rotate: distance * 1.5 },
              open: {
                x: distance * 30,
                y: Math.abs(distance) * 7 - (distance === 0 ? 8 : 0),
                rotate: distance * 8,
                transition: spring,
              },
            }}
          >
            <span>{label}</span>
          </motion.div>
        )
      })}
    </motion.div>
  )
}
